'use client'

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAccount } from 'wagmi';
import dynamic from 'next/dynamic';
import styles from '../styles/ClientPurchaseModal.module.css'; 
import { PurchaseModal } from './PurchaseModal'; // For crypto payments
import { CardPaymentModal } from './CardPaymentModal'; // Import the new CardPaymentModal
import SaftAgreementModal from './SaftAgreementModal'; // Import the SAFT modal

// Dynamically import MoonPayProvider only once, if needed at this level
const MoonPayProvider = dynamic(
    () => import('@moonpay/moonpay-react').then((mod) => mod.MoonPayProvider),
    { ssr: false }
);

interface ClientPurchaseModalProps {
    isOpen: boolean;
    onClose: () => void;
    projectUsdtWalletAddress?: string;
}

export const ClientPurchaseModal = ({ isOpen, onClose, projectUsdtWalletAddress }: ClientPurchaseModalProps) => {
    const { address, isConnected } = useAccount();
    const [isMounted, setIsMounted] = useState(false);
    const signatureRequestInFlight = useRef(false);

    // Payment method selection
    const [paymentMethodSelection, setPaymentMethodSelection] = useState<'crypto' | 'card' | null>(null);

    // Crypto Purchase Flow State
    const [showCryptoPurchaseModal, setShowCryptoPurchaseModal] = useState(false);
    const [initialCryptoStep, setInitialCryptoStep] = useState(1);
    const [isSaftModalOpen, setIsSaftModalOpen] = useState(false); // State for SAFT modal

    // Global UI Error (if needed, or handled within child components)
    const [globalUiError, setGlobalUiError] = useState<string | null>(null);
    const [globalIsLoading, setGlobalIsLoading] = useState(false); // If needed for shared loading states

    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Effect 1: Handles modal opening/closing and reset due to projectUsdtWalletAddress change.
    useEffect(() => {
        if (isOpen) {
            console.log('ClientPurchaseModal opened or projectUsdtWalletAddress changed. Project USDT Wallet:', projectUsdtWalletAddress);
            // Reset to payment selection screen.
            setPaymentMethodSelection(null);
            setShowCryptoPurchaseModal(false);
            setGlobalUiError(null);
            setGlobalIsLoading(false);
            setIsSaftModalOpen(false); // Close SAFT modal if open
            // Set initialCryptoStep based on current conditions when modal (re)opens to selection screen
            setInitialCryptoStep(isConnected && address ? 2 : 1);
        } else {
            // Reset when modal is closed
            setPaymentMethodSelection(null);
            setShowCryptoPurchaseModal(false);
            setIsSaftModalOpen(false); // Ensure SAFT modal is also closed
            setGlobalUiError(null);
            // console.log('ClientPurchaseModal closed.'); // Optional: for debugging
        }
    }, [isOpen, projectUsdtWalletAddress]); // Dependencies carefully chosen

    // Effect 2: Ensures initialCryptoStep is up-to-date if wallet status changes *while modal is already open*.
    useEffect(() => {
        if (isOpen) { // Only consider if modal is generally open
            // console.log(`ClientPurchaseModal: Wallet status update. isConnected: ${isConnected}, address: ${address}. Setting initialCryptoStep.`);
            setInitialCryptoStep(isConnected && address ? 2 : 1);
        }
    }, [isOpen, isConnected, address]); // Dependencies: isOpen, isConnected, address

    const handleGetSignatureForMoonPay = async (urlToSign: string): Promise<string> => {
        if (signatureRequestInFlight.current) {
            console.warn('[ClientPurchaseModal] Signature request already in flight for:', urlToSign);
            throw new Error("A signature request is already in progress. Please wait.");
        }

        signatureRequestInFlight.current = true;
        setGlobalIsLoading(true);
        setGlobalUiError(null);
        try {
            console.log('[ClientPurchaseModal] Requesting signature for:', urlToSign);
            const response = await fetch('/api/moonpay/generate-signed-url', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ urlToSign: urlToSign }),
            });
            const data = await response.json();
            if (!response.ok || !data.signature) {
                const errorMsg = data.error || 'Failed to get payment signature. Please try again.';
                throw new Error(errorMsg);
            }
            return data.signature;
        } catch (err: any) {
            console.error('Error getting MoonPay signature:', err);
            setGlobalUiError(err.message || 'Could not get payment signature.');
            throw err; // Re-throw to be caught by MoonPay widget/calling component
        } finally {
            signatureRequestInFlight.current = false;
            setGlobalIsLoading(false);
        }
    };

    const handleMoonPayTransactionCompleted = async (event: any) => {
        console.log('MoonPay Transaction Completed/Updated:', event);
        setGlobalIsLoading(true);
        setGlobalUiError(null);

        try {
            const response = await fetch('/api/moonpay/update-transaction', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    externalTransactionId: event.externalTransactionId,
                    transactionId: event.transactionId,
                    status: event.status,
                    currencyId: event.currencyId,
                    baseCurrencyAmount: event.baseCurrencyAmount,
                    quoteCurrencyAmount: event.quoteCurrencyAmount,
                    feeAmount: event.feeAmount,
                    networkFeeAmount: event.networkFeeAmount,
                    walletAddress: event.walletAddress, // The destination wallet (should be ours)
                    cardPaymentId: event.cardPaymentId, // If applicable
                    error: event.error // If any error
                }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to update transaction status with backend.');
            }
            console.log('Transaction status updated with backend:', data);
            if (event.status === 'completed' || event.status === 'succeeded') {
                // Handled by CardPaymentFlow or PurchaseModal for UI update
            } else if (event.status === 'failed') {
                setGlobalUiError(`Payment failed: ${event.error?.message || 'Unknown reason'}`);
                // UI update handled by child components
            }
        } catch (err: any) {
            console.error('Error updating transaction status:', err);
            setGlobalUiError(err.message || 'Could not update transaction status.');
        } finally {
            setGlobalIsLoading(false);
        }
    };

    const handleMoonPayLogin = async (props: { token: string; customerId: string; email: string }) => {
        console.log('MoonPay onAuthToken (onLogin) received:', props.token, props.customerId, props.email);
        setGlobalUiError(null);
        try {
            const response = await fetch('/api/moonpay/customer-login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    moonpayCustomerId: props.customerId, 
                    moonpayAuthToken: props.token, 
                    email: props.email 
                }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to process MoonPay login with backend.');
            }
            console.log('MoonPay login processed with backend:', data);
            // The email associated with the card payment flow should already be set within CardPaymentFlow
            // This callback can be used for more general updates if needed.
        } catch (err: any) {
            console.error('Error processing MoonPay login with backend:', err);
            setGlobalUiError(err.message || 'Could not sync MoonPay session.');
        }
    };

    const handleBackToPaymentSelection = () => {
        setPaymentMethodSelection(null);
        setShowCryptoPurchaseModal(false); // Ensure crypto modal is also hidden
        setGlobalUiError(null); // Clear any errors from previous flow
    };

    // New handler for closing the CardPaymentModal, which also resets payment selection
    const handleCloseCardModal = () => {
        setPaymentMethodSelection(null); // Go back to payment method selection
        onClose(); // Call the original onClose to close the ClientPurchaseModal if needed
    };

    if (!isOpen || !isMounted) return null;

    return (
        <MoonPayProvider apiKey={process.env.NEXT_PUBLIC_MOONPAY_PUBLISHABLE_KEY!} debug={process.env.NODE_ENV === 'development'}>
            <div className={styles.modalOverlay} onClick={onClose}>
                <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                    {!paymentMethodSelection && (
                        <>
                            <button onClick={onClose} className={styles.closeButtonTopRight}>&times;</button>
                            <h3>Select Payment Method</h3>
                            <div className={styles.paymentMethodSelector}>
                                <button onClick={() => { setPaymentMethodSelection('crypto'); setShowCryptoPurchaseModal(true); setIsSaftModalOpen(false); }} className={styles.paymentButton}>
                                    <span className={styles.buttonIcon}></span> Pay with Crypto
                                </button>
                                {/* <button onClick={() => setPaymentMethodSelection('card')} className={styles.paymentButton} disabled={!projectUsdtWalletAddress}>
                                <span className={styles.buttonIcon}></span> Pay with Card
                                    {!projectUsdtWalletAddress && <span className={styles.unavailableLabel}>(Setup Incomplete)</span>}
                                </button> */}
                                <button onClick={() => { setIsSaftModalOpen(true); setPaymentMethodSelection(null); setShowCryptoPurchaseModal(false);}} className={styles.paymentButton}>
                                    <span className={styles.buttonIcon}></span> SAFT Agreement
                                </button>
                            </div>
                            {globalUiError && <p className={styles.globalErrorText}>{globalUiError}</p>}
                        </>
                    )}

                    {paymentMethodSelection === 'crypto' && showCryptoPurchaseModal && (
                        <PurchaseModal
                            isOpen={showCryptoPurchaseModal} // Control crypto modal visibility specifically
                            onClose={() => { setShowCryptoPurchaseModal(false); setPaymentMethodSelection(null); /* Don't call main onClose here, let user return to selection */ }}
                            initialStep={initialCryptoStep}
                        />
                    )}

                    {paymentMethodSelection === 'card' && (
                        <CardPaymentModal
                            isOpen={paymentMethodSelection === 'card'} // Control visibility
                            onClose={handleCloseCardModal} // Use the new handler
                            onBackToPaymentSelection={handleBackToPaymentSelection}
                            projectUsdtWalletAddress={projectUsdtWalletAddress}
                            isMounted={isMounted}
                            onGetSignatureForMoonPay={handleGetSignatureForMoonPay}
                            onMoonPayTransactionCompleted={handleMoonPayTransactionCompleted}
                            onMoonPayLogin={handleMoonPayLogin}
                        />
                    )}
                    
                    {/* Render SAFT Modal independently of paymentMethodSelection for now */}
                    <SaftAgreementModal 
                        isOpen={isSaftModalOpen}
                        onClose={() => setIsSaftModalOpen(false)}
                        // You can prefill email/name if you have access to them here
                        // email={currentUserEmail}
                        // name={currentUserName}
                    />

                    {/* Global loading indicator if needed */}
                    {globalIsLoading && (
                        <div className={styles.loadingOverlayModalLevel}>
                            <div className={styles.spinner}></div>
                            <p>Processing...</p>
                        </div>
                    )}
                </div>
            </div>
        </MoonPayProvider>
    );
}; 